import React, { FC, ReactElement, useContext, useMemo } from 'react'
import { PresenceFilters } from '../Types/Types'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState } from '../../Common/Contexts'
import { ArrayUtils } from '../../../NonReact/utils/ArrayUtils'
import { Presence } from '../../../../i18n'
import Checkbox from '../../Common/Components/Checkbox'
import { DevicesResponse } from '../../../NonReact/Types/Types'

type Props = {
    filtersState: ReactState<PresenceFilters>
    departments: DevicesResponse[]
    queues: DevicesResponse[]
    securityGroups: DevicesResponse[]
}

export const PresenceMoreFilters: FC<Props> = ({
    filtersState: [filters, setFilters],
    departments,
    queues,
    securityGroups
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler

    const departmentCheckboxes: ReactElement[] = useMemo(
        (): ReactElement[] =>
            departments.map(
                (department: DevicesResponse, index: number): ReactElement => (
                    <div key={`department-${index}`} className='presence-filter'>
                        <Checkbox
                            labelRight={department.Description ?? department.DeviceNumber}
                            isChecked={filters.departments.includes(department.DeviceNumber)}
                            onChange={(checked: boolean): void => {
                                if (checked) {
                                    setFilters({
                                        ...filters,
                                        departments: ArrayUtils.removeDuplicates([
                                            ...filters.departments,
                                            department.DeviceNumber
                                        ])
                                    })
                                } else {
                                    setFilters({
                                        ...filters,
                                        departments: ArrayUtils.removeFromArray(
                                            ArrayUtils.removeDuplicates(filters.departments),
                                            department.DeviceNumber
                                        )
                                    })
                                }
                            }}
                        />
                    </div>
                )
            ),
        [JSON.stringify(departments), JSON.stringify(filters)]
    )

    const queueCheckboxes: ReactElement[] = useMemo(
        (): ReactElement[] =>
            queues.map(
                (queue: DevicesResponse, index: number): ReactElement => (
                    <div key={`queue-${index}`} className='presence-filter'>
                        <Checkbox
                            labelRight={queue.Description ?? queue.DeviceNumber}
                            isChecked={filters.queues.includes(queue.DeviceNumber)}
                            onChange={(checked: boolean): void => {
                                if (checked) {
                                    setFilters({
                                        ...filters,
                                        queues: ArrayUtils.removeDuplicates([...filters.queues, queue.DeviceNumber])
                                    })
                                } else {
                                    setFilters({
                                        ...filters,
                                        queues: ArrayUtils.removeFromArray(
                                            ArrayUtils.removeDuplicates(filters.queues),
                                            queue.DeviceNumber
                                        )
                                    })
                                }
                            }}
                        />
                    </div>
                )
            ),
        [JSON.stringify(queues), JSON.stringify(filters)]
    )

    const securityGroupsCheckboxes: ReactElement[] = useMemo(
        (): ReactElement[] =>
            securityGroups.map(
                (securityGroup: DevicesResponse, index: number): ReactElement => (
                    <div key={`security-groups-${index}`} className='presence-filter'>
                        <Checkbox
                            labelRight={securityGroup.Description ?? securityGroup.DeviceNumber}
                            isChecked={filters.securityGroups.includes(securityGroup.DeviceNumber)}
                            onChange={(checked: boolean): void => {
                                if (checked) {
                                    setFilters({
                                        ...filters,
                                        securityGroups: ArrayUtils.removeDuplicates([
                                            ...filters.securityGroups,
                                            securityGroup.DeviceNumber
                                        ])
                                    })
                                } else {
                                    setFilters({
                                        ...filters,
                                        securityGroups: ArrayUtils.removeFromArray(
                                            ArrayUtils.removeDuplicates(filters.securityGroups),
                                            securityGroup.DeviceNumber
                                        )
                                    })
                                }
                            }}
                        />
                    </div>
                )
            ),
        [JSON.stringify(queues), JSON.stringify(filters), JSON.stringify(departments), JSON.stringify(securityGroups)]
    )

    return (
        <div className='presence-filters'>
            <h3>{language.get(Presence.Presence_MoreFilters_Title)}</h3>
            {departments?.length > 0 ? (
                <div className='pf-filters-block'>
                    <h4>{language.get(Presence.Presence_MoreFilters_Options_Department)}</h4>
                    {departmentCheckboxes}
                </div>
            ) : (
                <></>
            )}
            {queues?.length > 0 ? (
                <div className='pf-filters-block'>
                    <h4>{language.get(Presence.Presence_MoreFilters_Options_Queue)}</h4>
                    {queueCheckboxes}
                </div>
            ) : (
                <></>
            )}
            {securityGroups?.length > 0 ? (
                <div className='pf-filters-block'>
                    <h4>{language.get(Presence.Presence_MoreFilters_Options_SecurityGroups)}</h4>
                    {securityGroupsCheckboxes}
                </div>
            ) : (
                <></>
            )}
        </div>
    )
}
